import React, { useState } from "react";
import { Card, ErrorMessage, toast } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import useUploadFilesToS3 from "../../../../common/hooks/uploadFilesToS3/useUploadFilesToS3";
import { PayeeActionTypes, PayeeStepActionTypes } from "../../Context/constants";
import {
  useBasePayeeReducerContext,
  usePayeeDispatchContext,
  usePayeeWorkflowDispatchContext,
} from "../../Context/PayeeContext";
import { formContainerStyles } from "../../styles_vanilla.css";
import { ClientDetails } from "../ClientDetails";
import { FormStepFooter } from "../FormStepFooter";
import PayeeDocumentUpload from "../PayeeDocumentUpload";
import { CustomErrorStyles } from "./styles_vanilla.css";
import { ClientDetailsAndDocumentStepProps } from "./types";

export const ClientDetailsAndDocumentStep: React.FC<ClientDetailsAndDocumentStepProps> = ({
  documentType,
  lockArena,
}) => {
  const stepDispatch = usePayeeWorkflowDispatchContext();
  const payeeDataDispatch = usePayeeDispatchContext();
  const { arenaId, arenaName, supportingDocuments, invoices } = useBasePayeeReducerContext();
  const documents = documentType === "invoice" ? invoices : supportingDocuments;
  const { uploadFilesToS3 } = useUploadFilesToS3();
  const [clientDetailsError, setClientDetailsError] = useState<string | null>(null);
  const [doc, setDoc] = useState<File | null>(documents?.[0]?.file || null);
  const [docError, setDocError] = useState<string | null>(null);
  const docCardTitle =
    documentType === "invoice" ? "Upload invoice" : "Upload supporting document";

  if (doc && docError) {
    setDocError(null);
  }
  if (clientDetailsError && arenaId && arenaName) {
    setClientDetailsError(null);
  }

  /**
   * Note: This is a temporary workaround to avoid the fact that TypeaheadInput used by ClientDetails is not
   * wrapped in a formik Field. So the state and error handling must be handled ourselves in the Submit handler
   * for the form. This is why we have no validation schema passed to the form or any initial values.
   */

  return (
    <div className={formContainerStyles}>
      <Form
        initialValues={{}}
        onSubmit={async () => {
          if (!arenaId || !arenaName) {
            setClientDetailsError("Please select a client");
          }
          if (!doc) {
            setDocError("Please upload a document");
          }
          if (!doc || !arenaId || !arenaName) {
            return;
          }
          // If the document hasn't changed AND we already have an uploaded doc (s3ObjectKey),
          // skip re-upload and proceed.
          if (doc === documents?.[0]?.file && Boolean(documents?.[0]?.s3ObjectKey)) {
            stepDispatch({ type: PayeeStepActionTypes.INCREMENT_STEP });
            return;
          }

          let uploadToS3Response;
          try {
            uploadToS3Response = await uploadFilesToS3([doc]);
          } catch (e) {
            addError(formatError(e));
            toast.error(`Failed to upload document. Please try again later`);
            return;
          }

          const dispatchDataPayload =
            documentType === "invoice"
              ? {
                  invoices: [
                    {
                      name: doc.name,
                      size: doc.size,
                      s3ObjectKey: uploadToS3Response[0].objectKey,
                      localUrl: URL.createObjectURL(doc),
                      file: doc,
                    },
                  ],
                }
              : {
                  supportingDocuments: [
                    {
                      name: doc.name,
                      size: doc.size,
                      s3ObjectKey: uploadToS3Response[0].objectKey,
                      localUrl: URL.createObjectURL(doc),
                      file: doc,
                    },
                  ],
                };

          // Dispatch to update document state in context.
          payeeDataDispatch({
            type:
              documentType === "invoice"
                ? PayeeActionTypes.SET_INVOICES
                : PayeeActionTypes.SET_SUPPORTING_DOCUMENTS,
            payload: dispatchDataPayload,
          });
          // Dispatch to increment the step in the context.
          stepDispatch({ type: PayeeStepActionTypes.INCREMENT_STEP });
        }}
      >
        <ColumnContainer>
          <Column size={"md"}>
            <PayeeDocumentUpload
              title={docCardTitle}
              onFileSelected={(file: File) => {
                setDoc(file);
              }}
              error={docError || undefined}
              initialFile={documents?.[0]?.file}
              onFileDeleted={() => {
                setDoc(null);
              }}
            />
            <Card title={"Client details"}>
              <ClientDetails disabled={lockArena} />
              {clientDetailsError && (
                <ErrorMessage className={CustomErrorStyles}>{clientDetailsError}</ErrorMessage>
              )}
            </Card>
          </Column>
        </ColumnContainer>
        <FormStepFooter />
      </Form>
    </div>
  );
};

export default ClientDetailsAndDocumentStep;
